/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useLabels } from '@/hooks/useLabels';
import { Distribution } from '../Distribution/Distribution';
import styles from './styles.module.scss';
import { Text, View } from '@az/starc-ui';
import { FlexGrid } from '@/components/FlexGrid/FlexGrid';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useSingleReviewStatistic } from '@/features/reviews/api/getReviewStatistics';

type RatingDistributionsProps = {
  header?: boolean;
  noReviews?: boolean;
  close?: VoidFunction;
};

const labelMap = {
  ratings: 'label_reviews_ratings',
};

const steps = new Array(5).fill(0);

export const RatingDistributions = ({
  header = false,
  noReviews = false,
  close,
}: RatingDistributionsProps) => {
  const labels = useLabels(labelMap);
  const { data: productDetails } = useProductDetails({ enabled: false });
  const product = productDetails?.product;
  const skuNumber = product?.itemId ?? '';
  const { data: reviewStatistic } = useSingleReviewStatistic({
    skuNumber,
    enabled: !!skuNumber,
  });

  const ratings = reviewStatistic?.ratingDistributionValues;

  return (
    <FlexGrid flexDirection="column" gap={3} className={styles.ratingsContainer}>
      {header && (
        <Text as="h2" className={styles.heading} weight="bold">
          {labels.ratings}
        </Text>
      )}
      {ratings ? (
        <View className={styles.distributionsContainer}>
          {steps.map((_, index) => {
            const star = 5 - index;
            const rating = ratings.find((rating) => rating.ratingValue == `${star}`);

            if (rating) {
              return (
                <Distribution
                  key={`rt-${index}`}
                  stars={rating.ratingValue}
                  average={noReviews ? '0' : rating.ratingValuePercentage}
                  noReviews={noReviews}
                  close={close}
                />
              );
            } else {
              return (
                <Distribution
                  key={`rt-${index}`}
                  stars={`${star}`}
                  average={'0'}
                  noReviews={true}
                  close={close}
                />
              );
            }
          })}
        </View>
      ) : null}
    </FlexGrid>
  );
};
